import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  Container,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { firestore } from "../Firebase";
import Bg from "../Images/hero.webp";
import { motion, AnimatePresence } from "framer-motion";
import { FaImages, FaCamera } from "react-icons/fa";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const snapshot = await firestore
          .collection("gallery")
          .orderBy("createdAt", "desc")
          .get();
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setImages(data);
      } catch (error) {
        console.error("Error fetching gallery: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: "cover",
        }}
      >
        <CircularProgress sx={{ color: "#00d9ff" }} size={60} />
        <Typography variant="h6" sx={{ color: "white", mt: 2 }}>
          Loading Gallery...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 30px rgba(0, 217, 255, 0.4)",
                }}
              >
                <FaImages size={40} color="white" />
              </Box>
            </motion.div>
          </Box>
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(90deg, #ffffff, #00d9ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Gallery
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.7)",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Capturing moments of innovation, collaboration, and success
          </Typography>
        </motion.div>

        {/* Gallery Grid */}
        <Grid container spacing={3}>
          {images.length > 0 ? (
            images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ y: -10 }}
                >
                  <Card
                    onClick={() => setSelectedImage(image)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "20px",
                      ...darkGlassStyle,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        border: "1px solid rgba(0, 217, 255, 0.5)",
                        boxShadow: "0 15px 40px rgba(0, 217, 255, 0.25)",
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", overflow: "hidden" }}>
                      <CardMedia
                        component="img"
                        height="220"
                        image={image.imageUrl}
                        alt={image.caption || "Gallery image"}
                        sx={{
                          objectFit: "cover",
                          transition: "transform 0.4s ease",
                          "&:hover": { transform: "scale(1.1)" },
                        }}
                      />
                      {/* Overlay on hover */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "linear-gradient(to top, rgba(0,0,36,0.8) 0%, transparent 50%)",
                          opacity: 0,
                          transition: "opacity 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            background: "rgba(0, 217, 255, 0.3)",
                            backdropFilter: "blur(10px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #00d9ff",
                          }}
                        >
                          <FaCamera size={20} color="#00d9ff" />
                        </Box>
                      </Box>
                    </Box>
                    {image.caption && (
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.8)",
                            textAlign: "center",
                            fontWeight: 500,
                          }}
                        >
                          {image.caption}
                        </Typography>
                      </CardContent>
                    )}
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  sx={{
                    borderRadius: "25px",
                    ...darkGlassStyle,
                    p: 6,
                    textAlign: "center",
                  }}
                >
                  <FaImages size={60} color="#00d9ff" style={{ marginBottom: 20 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: 'white' }}>
                    Gallery Coming Soon
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    Our moments and memories will be showcased here. Check back soon!
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Lightbox Dialog */}
      <AnimatePresence>
        {selectedImage && (
          <Dialog
            open={!!selectedImage}
            onClose={() => setSelectedImage(null)}
            maxWidth="lg"
            PaperProps={{
              sx: {
                backgroundColor: "transparent",
                boxShadow: "none",
                overflow: "visible",
              },
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ position: "relative" }}>
                <IconButton
                  onClick={() => setSelectedImage(null)}
                  sx={{
                    position: "absolute",
                    top: -50,
                    right: -10,
                    color: "white",
                    backgroundColor: "rgba(0, 217, 255, 0.3)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(0, 217, 255, 0.5)",
                    "&:hover": {
                      backgroundColor: "rgba(0, 217, 255, 0.5)",
                    },
                  }}
                >
                  <Close />
                </IconButton>
                <Box>
                  <img
                    src={selectedImage.imageUrl}
                    alt={selectedImage.caption || "Gallery image"}
                    style={{
                      maxWidth: "90vw",
                      maxHeight: "80vh",
                      borderRadius: 16,
                      boxShadow: "0 20px 60px rgba(0, 217, 255, 0.3)",
                      border: "3px solid rgba(0, 217, 255, 0.3)",
                    }}
                  />
                  {selectedImage.caption && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: "12px",
                        background: "rgba(0, 0, 36, 0.9)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(0, 217, 255, 0.3)",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {selectedImage.caption}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Gallery;
